import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { SUB_MODULE_KEYS_BY_PATH } from "../constants/moduleSubItems";
import * as routeNames from "./routeName";

/**
 * Pathless layout route used to gate a whole module's route group with the
 * module-permission system. When the module is missing the user lands on the
 * dashboard (the existing redirect target, see ProtectedRoute's mobileOnly
 * fallback) instead of a dead page.
 *
 * Two levels, finest wins:
 *   `allowed`  — the group's top-level module (derived from the sub-modules the
 *                user holds, so it answers "may they see this menu at all?").
 *   `hasKey`   — when the CURRENT path maps to one or more sub-modules, at
 *                least one of them must be held as well. Routes with no
 *                sub-module of their own are covered by `allowed` alone.
 * Pass no `hasKey` (or run in legacy mode) and the sub-module level is a no-op.
 *
 * This also does the "moved off a page you just lost" job: the guard re-renders
 * whenever the gate changes (an admin revoking a module refreshes it in place —
 * see refreshMyModules), so a user sitting on a page that has just become
 * unauthorised is redirected rather than left on it.
 *
 * Lives in its own module so it can be tested without importing appRoutes.js,
 * which pulls in every screen in the app.
 */
export const ModuleGuard = ({ allowed, hasKey, loading = false }) => {
	const { pathname } = useLocation()

	// Module data is loaded in the background. Returning null here unmounts the
	// entire current route on a hard refresh, leaving a white page whenever the
	// request is slow. Keep the existing route mounted until the authoritative
	// response arrives; then apply the normal redirect if access is denied.
	if (loading) return <Outlet />

	if (!allowed) return <Navigate to={routeNames.DASHBOARD_ROUTE} replace />

	if (hasKey) {
		const subModuleKeys = SUB_MODULE_KEYS_BY_PATH[pathname]
		if (subModuleKeys && !subModuleKeys.some(hasKey)) {
			return <Navigate to={routeNames.DASHBOARD_ROUTE} replace />
		}
	}

	return <Outlet />
}

export default ModuleGuard
